/**
 * GitHub Integrations Service
 * API client for GitHub App installation management
 */

import { apiClient } from '@/api/client'

// Types for GitHub integrations
export interface GitHubInstallation {
  id: string
  organizationId: string  // Transformed from organization_id by apiClient
  provider: 'github'
  installationId: number  // Transformed from installation_id by apiClient
  accountId: number  // Transformed from account_id by apiClient
  accountLogin: string  // Transformed from account_login by apiClient
  accountType: 'User' | 'Organization'  // Transformed from account_type by apiClient
  repositoryAccess: 'all' | 'selected'  // Transformed from repository_access by apiClient
  permissions: Record<string, string>
  events: string[]
  suspendedAt?: string  // Transformed from suspended_at by apiClient
  suspendedBy?: string  // Transformed from suspended_by by apiClient
  createdAt: string  // Transformed from created_at by apiClient
  updatedAt: string  // Transformed from updated_at by apiClient
}

export interface GitHubRepository {
  id: number
  name: string
  fullName: string  // Transformed from full_name by apiClient
  private: boolean
  description: string
  defaultBranch: string  // Transformed from default_branch by apiClient
  cloneUrl: string  // Transformed from clone_url by apiClient
  sshUrl: string  // Transformed from ssh_url by apiClient
  htmlUrl: string  // Transformed from html_url by apiClient
}

export interface ListInstallationsResponse {
  installations: GitHubInstallation[]
  total: number
  limit: number
  offset: number
}

export interface ListRepositoriesResponse {
  repositories: GitHubRepository[]
  total: number
}

class GitHubIntegrationsService {
  private readonly basePath = 'integrations/github'

  // List all GitHub installations for the organization
  async listInstallations(params?: Record<string, string>): Promise<ListInstallationsResponse> {
    return await apiClient.get<ListInstallationsResponse>(
      `${this.basePath}/installations`, params
    )
  }

  // Get a specific installation
  async getInstallation(installationId: string): Promise<GitHubInstallation> {
    return await apiClient.get<GitHubInstallation>(
      `${this.basePath}/installations/${installationId}`
    )
  }

  // List repositories accessible by an installation
  async listRepositories(installationId: string): Promise<ListRepositoriesResponse> {
    return await apiClient.get<ListRepositoriesResponse>(
      `${this.basePath}/installations/${installationId}/repositories`
    )
  }

  // Link a GitHub App installation to the organization
  async linkInstallation(githubInstallationId: number): Promise<{ status: string; message: string }> {
    return await apiClient.post<{ status: string; message: string }, { installation_id: number }>(
      `${this.basePath}/installations/link`,
      { installation_id: githubInstallationId }
    )
  }
}

export const githubIntegrationsService = new GitHubIntegrationsService()
